<template>
	<FormContainer
		class="form"
		:as-dialog
		:title="isEdit ? 'Edit Repository Config' : 'Create Repository Config'"
		:visible
		@close="emit('close')">
		<form @submit.prevent="submit">
			<FormInput
				label="Github Repository Url"
				required
				:error-message="formErrors.github_repository_url">
				<InputText
					v-model="formData.github_repository_url"
					:disabled="!!props.forceValues.github_repository_url" />
			</FormInput>
			<FormInput
				label="Github Token"
				:error-message="formErrors.github_token"
				:required="!isEdit">
				<template #label="slotProps">
					<div class="form__token-label-container">
						<label :for="slotProps.inputId">Github Token {{ isEdit ? '' : '*' }}</label>
						<a
							class="form__token-sublabel"
							href="https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens#creating-a-fine-grained-personal-access-token"
							target="_blank">
							Learn how to set up a fine-grained personal access token
						</a>
						<p class="form__token-sublabel">
							At least write access to the selected target repository is required
						</p>
					</div>
				</template>
				<template #default>
					<Password
						v-model="formData.github_token"
						fluid
						:disabled="!!props.forceValues.github_token"
						:feedback="false"
						:placeholder="isEdit ? 'Keep current token' : ''" />
				</template>
			</FormInput>
			<div class="form__footer-container">
				<Button
					v-if="isEdit && !props.hideRemove"
					icon="fal fa-trash"
					label="Remove"
					outlined
					severity="danger"
					:loading="loading"
					@click="remove" />
				<Button
					icon="fal fa-save"
					type="submit"
					:label="isEdit ? 'Update' : 'Create'"
					:loading="loading"
					@submit.prevent="submit" />
			</div>
		</form>
	</FormContainer>
</template>

<script setup lang="ts">
import { toRef, watch } from 'vue'
import { useRouter } from 'vue-router'
import RepositoryConfigsApi from '@/models/RepositoryConfig/Api'
import type { RepositoryConfig } from '@/models/RepositoryConfig/Model'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import { useForm } from '@/helpers/form'
import FormInput from '@/components/FormInput.vue'
import FormContainer from '@/components/FormContainer.vue'

type FormData = {
	github_repository_url: string
	github_token: string
	app_id: number
}

const emit = defineEmits<{
	(e: 'start-loading'): void
	(e: 'stop-loading'): void
	(e: 'close'): void
	(e: 'submit'): void
	(e: 'created'): void
	(e: 'updated'): void
	(e: 'deleted'): void
}>()

const props = withDefaults(
	defineProps<{
		id?: RepositoryConfig['id'] | null
		hideInputs?: (keyof FormData)[]
		defaultValues?: Partial<FormData>
		forceValues?: Partial<FormData>
		shouldRedirect?: boolean
		attachTo?: Record<string, { method: 'associate' | 'syncWithoutDetaching'; id: string | number }>
		asDialog?: boolean
		visible?: boolean
		hideRemove?: boolean
	}>(),
	{
		id: undefined,
		hideInputs: () => [],
		defaultValues: () => ({}),
		forceValues: () => ({}),
		shouldRedirect: true,
		attachTo: undefined,
		asDialog: false,
		visible: false,
		hideRemove: false,
	},
)

const router = useRouter()

const { formData, loading, formErrors, reset, submit, remove, isEdit } = useForm({
	api: () => new RepositoryConfigsApi(),
	defaultData: () => ({
		github_repository_url: '',
		github_token: '',
		app_id: 0,
	}),
	forceValues: () => props.forceValues,
	attachTo: () => props.attachTo,
	id: toRef(props, 'id'),
	onStartLoading: () => emit('start-loading'),
	onStopLoading: () => emit('stop-loading'),
	onSubmit: () => emit('submit'),
	onCreated: (entity) => {
		if (props.shouldRedirect) {
			router.push({ name: 'repository-configs-edit', params: { id: entity!.id } })
		}
		emit('created')
	},
	onUpdated: () => emit('updated'),
	onDeleted: () => emit('deleted'),
	formatOnUpdate: (data) => {
		if (data.github_token === '') {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { github_token, ...rest } = data
			return rest
		}
		return data
	},
})

watch(
	() => props.visible,
	(val) => {
		if (val) reset()
	},
)
</script>

<style lang="scss">
.form {
	form {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 10px;

		& > * {
			width: 100%;
		}

		.form__token-label-container {
			display: flex;
			flex-direction: column;
			gap: 5px;

			.form__token-sublabel {
				font-size: 12px;
			}
		}

		.form__footer-container {
			display: flex;
			justify-content: flex-end;
			align-items: center;
			gap: 10px;
		}

		&--edit {
			.form__footer-container {
				justify-content: space-between;
			}
		}
	}
}
</style>
